import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { InboxConversation, InboxImportProgress, InboxMessage } from '../types/inbox';
import { useT } from '../i18n';

type FilterOption = 'all' | 'unread' | 'replies';

// Resposta automática usada como mensagem pronta no chat
interface QuickReply {
  id: string;
  name: string;
  message: string;
  mediaUrl: string | null;
  mediaType: 'image' | 'video' | 'document' | null;
  fileName: string | null;
}

const CONVERSATION_POLL_MS = 10000;
const CONVERSATION_PAGE_SIZE = 100;
const MESSAGE_POLL_MS = 7000;

const FILTER_KEYS: Record<FilterOption, string> = {
  replies: 'inbox.filterReplies',
  unread: 'inbox.filterUnread',
  all: 'inbox.filterAll',
};

const SOURCE_KEYS: Record<string, string> = {
  CAMPAIGN: 'inbox.srcCampaign',
  FLOW: 'inbox.srcFlow',
  AUTO: 'inbox.srcAuto',
  MANUAL: '',
};

function formatTime(value: string): string {
  const date = new Date(value);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(value: string, t: (key: string) => string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return t('inbox.today');
  if (date.toDateString() === yesterday.toDateString()) return t('inbox.yesterday');
  return date.toLocaleDateString();
}

function formatListTime(value: string): string {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return formatTime(value);
  }
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}

// Muitos contatos foram importados com o próprio número como nome: nesse caso preferimos
// o nome que a pessoa usa no WhatsApp.
function isJustNumber(value: string | null | undefined): boolean {
  return !value || /^[+\d\s()-]+$/.test(value);
}

// O resumo da última mensagem é gravado em inglês pelo servidor; traduzimos na hora de mostrar
const PREVIEW_KEYS: Record<string, string> = {
  '📷 Image': 'media.image',
  '🎥 Video': 'media.video',
  '🎧 Audio': 'media.audio',
  '📄 Document': 'media.document',
  '🩵 Sticker': 'media.sticker',
  '📍 Location': 'media.location',
};

function previewText(preview: string | null, t: (key: string) => string): string {
  if (!preview) return '—';
  const key = PREVIEW_KEYS[preview.trim()];
  return key ? t(key) : preview;
}

function displayName(conversation: InboxConversation, t: (key: string) => string): string {
  if (conversation.isLid) {
    // Conversa antiga sem telefone: o nome do WhatsApp é a única identificação
    return conversation.contactName || t('inbox.waContact');
  }

  const contactName = conversation.contact?.nome;
  if (!isJustNumber(contactName)) return contactName!;
  if (!isJustNumber(conversation.contactName)) return conversation.contactName!;
  return contactName || conversation.contactName || `+${conversation.phone}`;
}

function initials(conversation: InboxConversation, t: (key: string) => string): string {
  const name = displayName(conversation, t).replace('+', '').trim();
  return name.slice(0, 2).toUpperCase();
}


const MEDIA_TYPES = ['image', 'video', 'audio', 'document', 'sticker'];

function MessageBubble({ message, onLoadMedia, t }: { message: InboxMessage; onLoadMedia: (id: string) => Promise<void>; t: (key: string, vars?: Record<string, string | number>) => string }) {
  const isInbound = message.direction === 'INBOUND';
  const sourceKey = !isInbound ? SOURCE_KEYS[message.source] : '';
  const sourceLabel = sourceKey ? t(sourceKey) : '';
  const [loadingMedia, setLoadingMedia] = useState(false);
  // Mensagens do histórico importado guardam o tipo, mas o arquivo é baixado quando alguém abre
  const needsMedia = !message.mediaUrl && MEDIA_TYPES.includes(message.type);

  const loadMedia = async () => {
    setLoadingMedia(true);
    try {
      await onLoadMedia(message.id);
    } finally {
      setLoadingMedia(false);
    }
  };

  return (
    <div className={`flex ${isInbound ? 'justify-start' : 'justify-end'} mb-2`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
          isInbound ? 'bg-white text-gray-800 rounded-bl-sm' : 'bg-emerald-100 text-gray-900 rounded-br-sm'
        }`}
      >
        {message.mediaUrl && message.type === 'image' && (
          <a href={message.mediaUrl} target="_blank" rel="noreferrer">
            <img src={message.mediaUrl} alt={message.fileName || 'image'} className="rounded-lg max-h-72 mb-1" />
          </a>
        )}

        {message.mediaUrl && message.type === 'video' && (
          <video src={message.mediaUrl} controls className="rounded-lg max-h-72 mb-1" />
        )}

        {message.mediaUrl && message.type === 'audio' && (
          <audio src={message.mediaUrl} controls className="mb-1" />
        )}

        {message.mediaUrl && message.type === 'document' && (
          <a
            href={message.mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-sm text-blue-700 underline mb-1"
          >
            📄 {message.fileName || 'Document'}
          </a>
        )}

        {needsMedia && (
          <button
            onClick={loadMedia}
            disabled={loadingMedia}
            className="flex items-center gap-2 text-xs text-blue-700 hover:text-blue-900 underline mb-1 disabled:opacity-50"
          >
            {loadingMedia ? t('common.loading') : `⬇️ ${t('inbox.loadMedia', { type: message.type })}`}
          </button>
        )}

        {!message.mediaUrl && !needsMedia && !['text'].includes(message.type) && (
          <div className="text-xs text-gray-500 italic mb-1">[{message.type}]</div>
        )}

        {message.content && (
          <div dir="auto" className="whitespace-pre-wrap break-words text-sm">{message.content}</div>
        )}

        <div className="flex items-center justify-end gap-2 mt-1">
          {sourceLabel && <span className="text-[10px] text-gray-500">{sourceLabel}</span>}
          <span className="text-[10px] text-gray-400">{formatTime(message.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

export function InboxPage() {
  const t = useT();
  const [conversations, setConversations] = useState<InboxConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<FilterOption>('replies');
  const [search, setSearch] = useState('');
  // Quantas conversas carregar; cresce quando o usuário pede mais
  const [limit, setLimit] = useState(CONVERSATION_PAGE_SIZE);
  const [loadingList, setLoadingList] = useState(true);

  const [searchParams] = useSearchParams();
  // /inbox?c=<id> abre direto a conversa (ex: vindo da análise da campanha)
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('c'));
  const [conversation, setConversation] = useState<InboxConversation | null>(null);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [quickReplies, setQuickReplies] = useState<QuickReply[] | null>(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [pendingMedia, setPendingMedia] = useState<Pick<QuickReply, 'mediaUrl' | 'mediaType' | 'fileName'> | null>(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState<InboxImportProgress | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedIdRef = useRef<string | null>(null);

  selectedIdRef.current = selectedId;

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiService.getInboxConversations({ filter, search: search || undefined, limit });
      setConversations(data.conversations);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || t('inbox.errConversations'));
    } finally {
      setLoadingList(false);
    }
  }, [filter, search, limit]);

  const loadMessages = useCallback(async (conversationId: string, showLoader = false) => {
    if (showLoader) setLoadingThread(true);
    try {
      const data = await apiService.getInboxMessages(conversationId, { limit: 100 });
      // Uma resposta antiga pode chegar depois de o usuário abrir outra conversa
      if (selectedIdRef.current !== conversationId) return;
      setConversation(data.conversation);
      setMessages(data.messages);
    } catch (err: any) {
      setError(err.message || t('inbox.errMessages'));
    } finally {
      if (showLoader) setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    setLimit(CONVERSATION_PAGE_SIZE);
  }, [filter, search]);

  // Lista de conversas: recarrega ao trocar de filtro/busca e a cada poucos segundos
  useEffect(() => {
    setLoadingList(true);
    loadConversations();
    const timer = setInterval(loadConversations, CONVERSATION_POLL_MS);
    return () => clearInterval(timer);
  }, [loadConversations]);

  // Conversa aberta
  useEffect(() => {
    if (!selectedId) {
      setConversation(null);
      setMessages([]);
      return;
    }

    loadMessages(selectedId, true);
    apiService.markInboxConversationRead(selectedId)
      .then(() => {
        setConversations(prev => prev.map(c => (c.id === selectedId ? { ...c, unreadCount: 0 } : c)));
      })
      .catch(() => undefined);

    const timer = setInterval(() => loadMessages(selectedId), MESSAGE_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const groupedMessages = useMemo(() => {
    const groups: Array<{ label: string; items: InboxMessage[] }> = [];
    messages.forEach(message => {
      const label = formatDateLabel(message.timestamp, t);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.items.push(message);
      } else {
        groups.push({ label, items: [message] });
      }
    });
    return groups;
  }, [messages]);

  const handleLoadMedia = async (messageId: string) => {
    try {
      const result = await apiService.downloadInboxMedia(messageId);
      setMessages(prev => prev.map(m => (m.id === messageId ? result.message : m)));
    } catch (err: any) {
      setError(err.message || t('inbox.errFile'));
    }
  };

  // Importação do histórico: dispara e acompanha o progresso até terminar
  const handleImport = async () => {
    setError(null);
    try {
      const started = await apiService.startInboxImport();
      setImporting(started);

      const timer = setInterval(async () => {
        try {
          const status = await apiService.getInboxImportStatus();
          setImporting(status);
          if (status.status !== 'RUNNING') {
            clearInterval(timer);
            loadConversations();
            if (status.status === 'ERROR') setError(status.error || t('inbox.errImport'));
            setTimeout(() => setImporting(null), 6000);
          }
        } catch {
          clearInterval(timer);
          setImporting(null);
        }
      }, 2000);
    } catch (err: any) {
      setImporting(null);
      setError(err.message || t('inbox.errImportStart'));
    }
  };

  // O anexo de uma mensagem pronta não segue para outra conversa
  useEffect(() => { setPendingMedia(null); }, [selectedId]);

  const openQuickReplies = async () => {
    setQuickOpen(open => !open);
    setQuickSearch('');
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` };
      const tenantId = localStorage.getItem('selected_tenant_id');
      if (tenantId) headers['X-Tenant-Id'] = tenantId;
      const response = await fetch('/api/auto-replies', { headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('inbox.errSend'));
      setQuickReplies(data);
    } catch (err: any) {
      setQuickReplies([]);
      setError(err.message);
    }
  };

  // Vai para a caixa de texto (e o anexo fica esperando): quem envia é a pessoa, depois de revisar
  const pickQuickReply = (reply: QuickReply) => {
    const firstName = (conversation?.contact?.nome || conversation?.contactName || '').trim().split(/\s+/)[0] || '';
    const text = reply.message
      .replace(/\{\{\s*(name|nome|الاسم)\s*\}\}/gi, firstName)
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/ +([،,!؟?.])/g, '$1')
      .trim();
    setReplyText(text);
    setPendingMedia(reply.mediaUrl ? { mediaUrl: reply.mediaUrl, mediaType: reply.mediaType || 'image', fileName: reply.fileName } : null);
    setQuickOpen(false);
  };

  const handleSend = async () => {
    const text = replyText.trim();
    if (!selectedId || (!text && !pendingMedia) || sending) return;

    setSending(true);
    setError(null);
    try {
      const result = await apiService.sendInboxReply(selectedId, pendingMedia
        ? { mediaUrl: pendingMedia.mediaUrl!, mediaType: pendingMedia.mediaType!, fileName: pendingMedia.fileName || undefined, caption: text || undefined }
        : { text });
      setMessages(prev => [...prev, result.message]);
      setReplyText('');
      setPendingMedia(null);
      loadConversations();
    } catch (err: any) {
      setError(err.message || t('inbox.errSend'));
    } finally {
      setSending(false);
    }
  };

  const handleAttachment = async (file: File) => {
    if (!selectedId) return;

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('inbox.errUpload'));
      }

      const uploaded = await response.json();
      const mimetype: string = uploaded.mimetype || '';
      const mediaType = mimetype.startsWith('image/')
        ? 'image'
        : mimetype.startsWith('video/')
          ? 'video'
          : mimetype.startsWith('audio/')
            ? 'audio'
            : 'document';

      const result = await apiService.sendInboxReply(selectedId, {
        mediaUrl: uploaded.fileUrl,
        mediaType,
        mediaMimeType: mimetype,
        fileName: uploaded.originalName,
        caption: replyText.trim() || undefined,
      });

      setMessages(prev => [...prev, result.message]);
      setReplyText('');
      loadConversations();
    } catch (err: any) {
      setError(err.message || t('inbox.errAttachment'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-4 sm:p-6 h-[calc(100vh-1rem)] flex flex-col">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('inbox.title')}</h1>
          <p className="text-sm text-gray-500">{t('inbox.subtitle')}</p>
        </div>

        <div className="flex items-center gap-3">
          {importing && importing.status === 'RUNNING' && (
            <span className="text-xs text-gray-500">
              {t('inbox.importing', { done: importing.processed || 0, total: importing.total || 0 })}
            </span>
          )}
          {importing && importing.status === 'DONE' && (
            <span className="text-xs text-emerald-600">
              {t('inbox.imported', { messages: importing.imported || 0, conversations: importing.conversations || 0 })}
            </span>
          )}
          <button
            onClick={handleImport}
            disabled={importing?.status === 'RUNNING'}
            title={t('inbox.importTitle')}
            className="px-3 py-2 text-sm rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {importing?.status === 'RUNNING' ? t('inbox.importingShort') : t('inbox.import')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Lista de conversas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col min-h-0">
          <div className="p-3 border-b border-gray-100 space-y-3">
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('inbox.search')}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            <div className="flex gap-1">
              {(['replies', 'unread', 'all'] as FilterOption[]).map(option => (
                <button
                  key={option}
                  onClick={() => setFilter(option)}
                  className={`flex-1 text-xs py-1.5 rounded-lg transition-colors ${
                    filter === option ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(FILTER_KEYS[option])}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingList && conversations.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400">{t('common.loading')}</div>
            )}

            {!loadingList && conversations.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400">{t('inbox.empty')}<div className="mt-2 text-xs">{t('inbox.emptyHint')}</div>
              </div>
            )}

            {conversations.map(item => (
              <button
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`w-full text-left px-3 py-3 border-b border-gray-50 flex gap-3 items-center transition-colors ${
                  selectedId === item.id ? 'bg-emerald-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold shrink-0">
                  {initials(item, t)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span dir="auto" className="font-medium text-sm text-gray-900 truncate">{displayName(item, t)}</span>
                    <span className="text-[11px] text-gray-400 shrink-0">{formatListTime(item.lastMessageAt)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span dir="auto" className="text-xs text-gray-500 truncate">
                      {item.lastDirection === 'OUTBOUND' && <span className="text-gray-400">{t('inbox.you')} </span>}
                      {previewText(item.lastPreview, t)}
                    </span>
                    {item.unreadCount > 0 && (
                      <span className="bg-emerald-500 text-white text-[10px] rounded-full px-1.5 py-0.5 shrink-0">
                        {item.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between gap-2">
            <span className="text-[11px] text-gray-400">
              {t('inbox.conversationsCount', { shown: conversations.length, total })}
            </span>
            {conversations.length < total && (
              <button
                onClick={() => setLimit(current => current + CONVERSATION_PAGE_SIZE)}
                className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium"
              >{t('inbox.loadMore')}</button>
            )}
          </div>
        </div>

        {/* Conversa */}
        <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 flex flex-col min-h-0">
          {!selectedId && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">{t('inbox.selectConversation')}</div>
          )}

          {selectedId && (
            <>
              <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-xl flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div dir="auto" className="font-semibold text-gray-900 truncate">
                    {conversation ? displayName(conversation, t) : '…'}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {conversation ? (conversation.isLid ? t('inbox.noPhone') : `+${conversation.phone}`) : ''}
                    {conversation?.session ? ` · ${conversation.session.displayName || conversation.session.name}` : ''}
                  </div>
                </div>
                {conversation?.contact?.optedOut && (
                  <span className="text-[11px] bg-red-100 text-red-700 px-2 py-1 rounded-full shrink-0">{t('inbox.unsubscribed')}</span>
                )}
                {conversation?.isLid && (
                  <span className="text-[11px] bg-gray-200 text-gray-700 px-2 py-1 rounded-full shrink-0">{t('inbox.readOnly')}</span>
                )}
                {conversation && !conversation.isLid && !conversation.contact && (
                  <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-1 rounded-full shrink-0">{t('inbox.notInContacts')}</span>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3">
                {loadingThread && messages.length === 0 && (
                  <div className="text-center text-sm text-gray-400 py-6">{t('common.loading')}</div>
                )}

                {groupedMessages.map(group => (
                  <div key={group.label}>
                    <div className="flex justify-center my-3">
                      <span className="text-[11px] bg-white text-gray-500 px-3 py-1 rounded-full shadow-sm">
                        {group.label}
                      </span>
                    </div>
                    {group.items.map(message => (
                      <MessageBubble key={message.id} message={message} onLoadMedia={handleLoadMedia} t={t} />
                    ))}
                  </div>
                ))}

                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-gray-200 bg-white rounded-b-xl p-3">
                {conversation?.isLid ? (
                  <div className="text-center text-xs text-gray-500 py-2">{t('inbox.readOnlyHint')}</div>
                ) : (
                <>
                {pendingMedia && (
                  <div className="flex items-center gap-2 mb-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                    {pendingMedia.mediaType === 'image' && pendingMedia.mediaUrl
                      ? <img src={pendingMedia.mediaUrl} alt="" className="h-10 w-10 object-cover rounded" />
                      : <span>📎</span>}
                    <span className="flex-1 min-w-0 truncate text-gray-600" dir="ltr">{pendingMedia.fileName || pendingMedia.mediaType}</span>
                    <button onClick={() => setPendingMedia(null)} className="text-red-600">{t('inbox.quickRemoveMedia')}</button>
                  </div>
                )}
                {/\[[^\]\n]{1,60}\]/.test(replyText) && (
                  <div className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">⚠ {t('inbox.quickFillIn')}</div>
                )}
                <div className="relative flex items-end gap-2">
                  {quickOpen && (
                    <div className="absolute bottom-12 start-0 z-20 w-80 max-w-full bg-white border border-gray-200 rounded-xl shadow-xl p-2">
                      <input
                        value={quickSearch}
                        onChange={event => setQuickSearch(event.target.value)}
                        placeholder={t('inbox.quickSearch')}
                        autoFocus
                        className="w-full mb-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                      />
                      <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                        {quickReplies === null && <div className="p-3 text-xs text-gray-500">{t('common.loading')}</div>}
                        {quickReplies?.length === 0 && <div className="p-3 text-xs text-gray-500">{t('inbox.quickEmpty')}</div>}
                        {(quickReplies || [])
                          .filter(reply => `${reply.name} ${reply.message}`.toLowerCase().includes(quickSearch.trim().toLowerCase()))
                          .map(reply => (
                            <button key={reply.id} onClick={() => pickQuickReply(reply)} className="w-full text-start p-2 hover:bg-emerald-50 rounded">
                              <div className="text-sm font-medium text-gray-900" dir="auto">{reply.name}{reply.mediaUrl ? ' 📎' : ''}</div>
                              <div className="text-xs text-gray-500 line-clamp-2" dir="auto">{reply.message}</div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={openQuickReplies}
                    disabled={uploading || sending}
                    title={t('inbox.quickReplies')}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-50"
                  >
                    ⚡
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={event => {
                      const file = event.target.files?.[0];
                      if (file) handleAttachment(file);
                    }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || sending}
                    title={t('inbox.attach')}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-50"
                  >
                    📎
                  </button>
                  <textarea
                    value={replyText}
                    onChange={event => setReplyText(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={1}
                    dir="auto"
                    placeholder={t('inbox.reply')}
                    className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 max-h-32"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || uploading || (!replyText.trim() && !pendingMedia)}
                    className="px-4 h-10 rounded-full bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-50"
                  >
                    {sending ? t('inbox.sending') : t('inbox.send')}
                  </button>
                </div>
                </>
                )}
                {uploading && <div className="text-xs text-gray-500 mt-2">{t('inbox.uploading')}</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default InboxPage;
